import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { mascararMoeda, moedaDeNumero } from "../lib/mascaras";
import { PASSOS_TOTAIS, reaisCurto, type Escala } from "../lib/bancada";

/**
 * A BANCADA — a forma das telas de cálculo do Radar.
 *
 * ---------------------------------------------------------------------------
 * O QUE ESTAVA ERRADO, E NÃO ERA ENFEITE
 * ---------------------------------------------------------------------------
 * As quatro calculadoras nasceram com a forma "formulário": campos em cima,
 * resultado embaixo, aparecendo depois que tudo estava preenchido. O gesto real
 * do contador nas quatro é o mesmo — **mexer num número e ver o outro mudar**:
 *
 *   Fator R   → "quanta folha eu preciso ter para cair no Anexo III?"
 *   Alíquota  → "quanto eu pago se faturar mais?"
 *   Custo CLT → "vale a pena contratar por este salário?"
 *   Reforma   → "quanto muda para este cliente?"
 *
 * Na forma antiga isso custa: digitar tudo → rolar → ler → subir → apagar →
 * redigitar. Quatro dessas voltas e a pessoa desiste e abre o Excel.
 *
 * A bancada muda três coisas:
 *
 *  1. **O resultado nunca sai da vista.** No desktop ele GRUDA (sticky) e
 *     acompanha a rolagem; no celular, uma fita no rodapé aparece assim que o
 *     painel sai de vista. É a peça que resolve o problema de verdade — o resto
 *     é consequência.
 *  2. **Arrastar, não só digitar.** Explorar com o dedo é o gesto de "e se?";
 *     digitar é o gesto de "o número é este". Os dois no MESMO controle, porque
 *     separá-los obrigaria a escolher errado uma das duas vezes.
 *  3. **A conta é ao vivo.** Sem botão "calcular": ele existe quando a conta é
 *     cara, e esta é aritmética em memória.
 *
 * ---------------------------------------------------------------------------
 * A ALTURA DO TOPO NÃO SE CRAVA
 * ---------------------------------------------------------------------------
 * O `sticky` desconta `--cf-topo`, que é a altura MEDIDA do `Topo` — a mesma
 * variável que `lib/rolarPara.ts` usa. Cravar 56 ou 72 à mão deixa o painel
 * enterrado atrás do topo ou flutuando com um vão, e quebra no dia em que o
 * `Topo` mudar de altura.
 */

// ---------------------------------------------------------------------------
// A manopla — arrastar e digitar, no mesmo controle
// ---------------------------------------------------------------------------

export function Manopla({
  id,
  rotulo,
  valor,
  aoMudar,
  escala,
  dica,
  sufixoDoCampo,
  atalhos,
}: {
  id: string;
  rotulo: ReactNode;
  valor: number | null;
  aoMudar: (n: number | null) => void;
  escala: Escala;
  dica?: ReactNode;
  /** Texto curto à direita do campo — "por mês", "nos 12 meses". */
  sufixoDoCampo?: string;
  /** Valores comuns, para chegar num clique. */
  atalhos?: { rotulo: string; valor: number }[];
}) {
  const [texto, setTexto] = useState(() => moedaDeNumero(valor));
  const trilhoId = `${id}-trilho`;

  // Quando o valor vem de fora (o trilho, um atalho, "limpar"), o campo
  // acompanha. Os dois lugares que leem a fonte existem de propósito: sem o
  // efeito o trilho não move o campo; sem o estado inicial o campo pisca vazio.
  useEffect(() => {
    setTexto((atual) =>
      // Só sobrescreve quando o número mudou de verdade — senão digitar
      // "1.234,5" viraria "R$ 1.234,50" no meio da digitação.
      mascararMoeda(atual).numero === valor ? atual : moedaDeNumero(valor),
    );
  }, [valor]);

  const n = valor ?? 0;
  const posicao = escala.posicaoDoValor(n);
  const pct = (posicao / PASSOS_TOTAIS) * 100;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-tinta">
          {rotulo}
        </label>
        <div className="flex items-center gap-2">
          <input
            id={id}
            inputMode="numeric"
            placeholder="R$ 0,00"
            value={texto}
            onChange={(e) => {
              const m = mascararMoeda(e.target.value);
              setTexto(m.texto);
              aoMudar(m.numero);
            }}
            className="w-40 rounded-cf border border-borda bg-superficie px-3 py-1.5 text-right text-sm font-semibold text-tinta tabular-nums outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
          />
          {sufixoDoCampo && <span className="text-xs text-cinza">{sufixoDoCampo}</span>}
        </div>
      </div>

      {/* O trilho. `aria-valuetext` diz o valor EM REAIS: sem ele o leitor de
          tela anuncia "237", que é a posição — um número que não existe para
          quem está usando. */}
      <div className="relative pt-1">
        <input
          id={trilhoId}
          type="range"
          min={0}
          max={PASSOS_TOTAIS}
          step={1}
          value={posicao}
          aria-label={typeof rotulo === "string" ? rotulo : undefined}
          aria-valuetext={moedaDeNumero(n)}
          onChange={(e) => aoMudar(escala.valorDaPosicao(Number(e.target.value)))}
          className="w-full cursor-pointer accent-acento"
          style={{ /* o preenchimento até a manopla, sem depender de pseudo-elemento */
            background: `linear-gradient(to right, var(--cf-acento) ${pct}%, var(--cf-borda) ${pct}%)`,
            height: "0.375rem",
            borderRadius: "9999px",
            appearance: "auto",
          }}
        />
        {/* As marcas das faixas legais. `aria-hidden` porque a informação já
            está no `aria-valuetext` e nas etiquetas abaixo — repetir seis
            números soltos no leitor de tela é ruído, não acesso. */}
        {escala.marcas.length > 0 && (
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-1">
            {escala.marcas.map((m) => (
              <span
                key={m.valor}
                className="absolute top-0 h-1.5 w-px bg-tinta/30"
                style={{ left: `${m.pct}%` }}
              />
            ))}
          </div>
        )}
      </div>

      {escala.marcas.length > 0 && (
        <div aria-hidden className="relative h-4">
          {escala.marcas.map((m) => (
            <span
              key={m.valor}
              className="absolute -translate-x-1/2 text-[10px] tabular-nums text-cinza"
              style={{ left: `${m.pct}%` }}
            >
              {reaisCurto(m.valor)}
            </span>
          ))}
        </div>
      )}

      {atalhos && atalhos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {atalhos.map((a) => (
            <button
              key={a.rotulo}
              type="button"
              onClick={() => aoMudar(a.valor)}
              className="rounded-full border border-borda bg-superficie px-2.5 py-1 text-xs font-medium text-cinza transition hover:border-acento/40 hover:text-link"
            >
              {a.rotulo}
            </button>
          ))}
        </div>
      )}

      {dica && <p className="text-xs text-cinza">{dica}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A régua de alvo — para quando existe um NÚMERO a alcançar
// ---------------------------------------------------------------------------

/**
 * Uma barra que enche até a marca do alvo. Serve ao Fator R, onde a pergunta é
 * "cheguei nos 28%?" e a resposta em texto ("24,3%") não diz quanto falta.
 *
 * A cor é semântica e troca com o tema — `sucesso` quando alcançou, `alerta`
 * quando não. Cor de MARCA aqui ficaria invisível num dos dois: medido,
 * `bg-azul` dá 14,57 no claro e 1,07 no escuro.
 */
export function ReguaDeAlvo({
  atual,
  alvo,
  maximo,
  rotuloAlvo,
  alcancou,
}: {
  atual: number;
  alvo: number;
  maximo: number;
  rotuloAlvo: string;
  alcancou: boolean;
}) {
  const teto = maximo > 0 ? maximo : 1;
  const pctAtual = Math.max(0, Math.min((atual / teto) * 100, 100));
  const pctAlvo = Math.max(0, Math.min((alvo / teto) * 100, 100));
  return (
    <div className="space-y-1.5">
      <div className="relative h-3 w-full overflow-hidden rounded-full bg-chip">
        <div
          className={`h-full rounded-full transition-all ${alcancou ? "bg-sucesso" : "bg-laranja"}`}
          style={{ width: `${pctAtual}%` }}
        />
        {/* A marca do alvo por cima do preenchimento, com contraste próprio:
            uma linha da cor do texto sobre qualquer um dos dois fundos. */}
        <span
          aria-hidden
          className="absolute top-0 h-full w-0.5 bg-tinta"
          style={{ left: `${pctAlvo}%` }}
        />
      </div>
      <div aria-hidden className="relative h-4">
        <span
          className="absolute -translate-x-1/2 text-[10px] font-medium tabular-nums text-tinta"
          style={{ left: `${pctAlvo}%` }}
        >
          {rotuloAlvo}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A bancada
// ---------------------------------------------------------------------------

export function Bancada({
  controles,
  resultado,
  fita,
}: {
  controles: ReactNode;
  resultado: ReactNode;
  /** O resumo de UMA linha que vai para a fita do celular. Nulo = sem fita. */
  fita?: { rotulo: string; valor: string; apoio?: string; alcancou?: boolean } | null;
}) {
  const alvo = useRef<HTMLDivElement | null>(null);
  const [resultadoNaTela, setResultadoNaTela] = useState(true);

  // A fita só aparece quando o painel de resultado SAIU de vista. Mostrá-la
  // sempre seria uma segunda cópia do mesmo número na mesma tela — a cicatriz
  // do "índice em dois lugares", que custou 8.249px ao IR Pro.
  useEffect(() => {
    const el = alvo.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([e]) => setResultadoNaTela(e.isIntersecting), {
      // A fita mede ~64px; sem a margem ela pisca ao encostar na própria fita.
      rootMargin: "0px 0px -72px 0px",
      threshold: 0.15,
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const mostrarFita = Boolean(fita) && !resultadoNaTela;

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-5">{controles}</div>

        {/* No desktop o painel gruda abaixo do Topo; no celular ele é só o
            próximo bloco da página. `lg:self-start` é obrigatório: sem ele o
            item do grid estica na altura da linha e o `sticky` não tem folga
            para deslizar — o painel fica parado e ninguém entende por quê. */}
        <div ref={alvo} className="lg:sticky lg:self-start" style={{ top: "calc(var(--cf-topo) + 1rem)" }}>
          {resultado}
        </div>
      </div>

      {/* A fita. `role="status"` e não `aria-live="assertive"`: o número muda a
          cada arrasto, e um leitor de tela interrompendo a cada passo torna o
          controle inutilizável para quem depende dele. */}
      {fita && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed inset-x-0 bottom-0 z-30 border-t border-borda bg-superficie/95 px-4 py-3 backdrop-blur transition-transform lg:hidden ${
            mostrarFita ? "translate-y-0" : "translate-y-full"
          }`}
          // Some do foco e do leitor quando está escondida: uma barra fora da
          // tela que ainda recebe Tab é uma armadilha de teclado.
          aria-hidden={!mostrarFita}
          inert={!mostrarFita}
        >
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.1em] text-cinza">{fita.rotulo}</p>
              {fita.apoio && <p className="truncate text-xs text-cinza">{fita.apoio}</p>}
            </div>
            <p
              className={`shrink-0 text-2xl font-bold tabular-nums ${
                fita.alcancou === undefined
                  ? "text-titulo"
                  : fita.alcancou
                    ? "text-aviso-tinta"
                    : "text-alerta-tinta"
              }`}
            >
              {fita.valor}
            </p>
          </div>
        </div>
      )}

      {/* O respiro para a fita não cobrir o fim da página. Só no celular, e só
          quando há fita. */}
      {fita && <div aria-hidden className="h-16 lg:hidden" />}
    </>
  );
}

// ---------------------------------------------------------------------------
// O painel de resultado
// ---------------------------------------------------------------------------

/**
 * O painel que gruda. Um número herói, um apoio e o detalhamento.
 *
 * O fundo é `superficie` e não navy: ele fica ao lado dos controles, e um bloco
 * navy de 22rem colado na direita compete com a `Cabine` que já é navy no alto
 * da mesma tela — duas manchas escuras disputando a atenção numa tela cujo
 * assunto é um número.
 */
export function PainelDeResultado({
  rotulo,
  valor,
  apoio,
  tom = "neutro",
  children,
  aviso,
}: {
  rotulo: string;
  valor: string;
  apoio?: ReactNode;
  tom?: "neutro" | "sucesso" | "alerta";
  children?: ReactNode;
  aviso?: ReactNode;
}) {
  const cor =
    tom === "sucesso" ? "text-aviso-tinta" : tom === "alerta" ? "text-alerta-tinta" : "text-titulo";
  return (
    <section
      data-painel-resultado
      className="rounded-cf border border-borda bg-superficie p-4 shadow-sm"
    >
      <p className="text-[11px] uppercase tracking-[0.12em] text-cinza">{rotulo}</p>
      <p className={`mt-1 text-4xl font-bold tabular-nums ${cor}`}>{valor}</p>
      {apoio && <div className="mt-1.5 text-sm text-cinza">{apoio}</div>}
      {children && <div className="mt-4 space-y-2 border-t border-borda pt-3">{children}</div>}
      {aviso && (
        <div className="mt-3 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-3 py-2 text-xs text-alerta-tinta">
          {aviso}
        </div>
      )}
    </section>
  );
}

/** O estado "ainda não dá para calcular", no lugar do painel. */
export function PainelVazio({ children }: { children: ReactNode }) {
  return (
    <section
      data-painel-resultado
      className="rounded-cf border border-dashed border-borda bg-superficie/60 p-4 text-sm text-cinza"
    >
      {children}
    </section>
  );
}

/** Um par rótulo/valor dentro do painel. */
export function LinhaDoPainel({
  rotulo,
  valor,
  forte,
}: {
  rotulo: ReactNode;
  valor: string;
  forte?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className={forte ? "font-medium text-tinta" : "text-cinza"}>{rotulo}</span>
      <span className={`shrink-0 tabular-nums ${forte ? "font-semibold text-tinta" : "text-tinta"}`}>
        {valor}
      </span>
    </div>
  );
}

/**
 * Um grupo de controles com título — a "gaveta" da bancada.
 *
 * `<section role="group" aria-labelledby>` e NÃO `<fieldset><legend>`: a
 * especificação posiciona o `legend` SOBRE a borda, e com o card arredondado da
 * casa o título sai flutuando fora da caixa. O leitor de tela recebe a mesma
 * associação por aqui.
 */
export function GavetaDeControles({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section
      role="group"
      aria-labelledby={id}
      className="rounded-cf border border-borda bg-superficie p-4"
    >
      <h3 id={id} className="text-sm font-semibold text-titulo">
        {titulo}
      </h3>
      {descricao && <p className="mt-0.5 text-xs text-cinza">{descricao}</p>}
      <div className="mt-4 space-y-5">{children}</div>
    </section>
  );
}
